import {connect} from 'react-redux';
import React, {Component} from 'react';
import TagManager from 'react-gtm-module';
import classNames from 'classnames';
import injectSheet from 'react-jss';
import qs from 'query-string';

import {get} from '-/util/lodash';
import config from '-/config/config';
import ErrorBoundary from '-/components/general/ErrorBoundary';
import Head from '-/components/general/Head';
import Normalize from '-/components/general/Normalize';
import Steps from '-/components/general/Steps';
import {AppSteps} from '-/components/general/AppData';
import Toast from '-/components/general/Toast';
import Header from './Header';
import CallMeFooter from '-/components/general/CallMeFooter';
import {isReferrer} from '-/util/util';
import Loader from '-/components/general/Loader';
import DialogContainer from '-/components/dialog/DialogContainer';
import DialogService from '-/services/DialogService';
import SessionService from '-/services/SessionService';
import UIService from '-/services/UIService';
import {fetchInfo} from '-/actions/actions';
import {getDocumentElement, isMeLanding, isNewLanding, isNewOffer, isNewCashOffer, isNewFinanceOffer} from '-/util/util';
import {getCsrfToken} from '-/util/axios';
import StepLookup from '-/components/general/StepLookup';
import greetingStyles from '-/styles/sections/greeting';
import {duplicateOfferSetData} from '-/util/authenticate';
import MCCFooter from '-/components/general/MCCFooter';

const {mode} = config;
const isDev = mode === 'development';
const queryObj = qs.parse(location.search);
const {
    campaign = '',
    offerId,
    segmentId = ''
} = queryObj;
const styles = theme => ({
    ...greetingStyles(theme),
    main: {
        position: 'relative',
        minHeight: '100%',
        maxWidth: 768,
        paddingBottom: 60,
        margin: '0 auto',
        backgroundColor: '#ffffff'
    },
    stips: {
        background: 'white',
        borderStyle: 'solid',
        borderColor: theme.colors.primary
    },
    welcome: {
        borderWidth: 5,
        borderColor: theme.colors.primary
    }
});

class App extends Component {
    constructor(props) {
        super(props);

        this.state = {
            googleTagManagerSet: false
        };
    }

    async componentDidMount() {
        const authenticated = SessionService.authenticated();
        const {baseOffer, offerId, ...restOfParams} = queryObj;
        const params = qs.stringify(restOfParams);

        if (baseOffer && isNewOffer()) {
            if (!authenticated) {
                window.location.href = `/?offerId=${baseOffer}&copyOffer&${params}`;
            } else {
                const {otherLoanId} = queryObj;
                await duplicateOfferSetData({otherLoanId});
            }
        }
    }

    componentDidUpdate() {
        const {googleTagManagerSet} = this.state;

        if (!googleTagManagerSet) {
            const gtmId = get(this, 'props.googleTagManagerId');

            if (gtmId) {
                const tagManagerArgs = {gtmId};
                TagManager.initialize(tagManagerArgs);

                this.setState({
                    googleTagManagerSet: true
                });
            }
        }
    }

    componentWillReceiveProps(nextProps) {
        const {currentStep, idle, isBack, previousY} = this.props;
        if (currentStep !== 'hero' && nextProps.idle && !idle) {
            this.showAlert();
        }

        if (nextProps.isBack && !isBack) {
            this.forceUpdate(() => {
                document.body.scrollTop = getDocumentElement().scrollTop = previousY;
            });
        }
    }

    showAlert() {
        const {dialog} = this.props;
        if (dialog !== 'InactivityAlertDialog') {
            DialogService.show('InactivityAlertDialog');
        }
    }

    renderError() {
        const {error, classes = {}} = this.props;
        if (!error) {
            return;
        }

        let link;
        if (error.contactPhone) {
            link = <a href={`tel:${error.contactPhone}`}>phone us</a>;
        } else {
            link = <a href="mailto:support@deliveriesondemand.com">email us</a>;
        }

        let msg = 'There was a problem.';

        if (error.isCustomError) {
            msg = error.message;
        }

        return (
            <section className={classes.root}>
                <div>
                    <h1>Oops!</h1>
                    <h2>{msg}</h2>
                    <br />
                    <hr className={classes.hr} />
                    To proceed, please {link}
                </div>
            </section>
        );
    }

    render() {
        const {initialized, isCU, currentStep, loading, classes = ''} = this.props;
        const isNew = isNewOffer();
        const showNewOffer = (isNew && currentStep === 'hero');
        const newOfferNextStep = isNewCashOffer() || isNewFinanceOffer() ? 'buyerInfo' : 'newOffer';
        let step = AppSteps[showNewOffer ? newOfferNextStep : currentStep];

        getCsrfToken(true);

        if (!step) return null;

        const mainClass = classNames(step.cls, classes.main, {
            [classes.stips]: ['stipsWelcome', 'stipResubmit'].includes(currentStep),
            [classes.welcome]: currentStep === 'stipsWelcome'
        });

        const isCallBack = currentStep === 'callback';
        const overrideError =  isCallBack || (isNew || isReferrer(campaign));
        const isAllowedLanding = isNewLanding() || isMeLanding();
        const showStepLookup = initialized || overrideError || segmentId || isAllowedLanding;
        const pageType = UIService.getPageType();
        // The reduction pages are the default page with different copy, so they keep
        // the footer the default page has; `compact` and the gift-card pages replace it.
        const footerPageTypes = ['default', 'me', 'paymentReduction', 'rateReduction', 'stips'];
        const hideFooter = pageType && !footerPageTypes.includes(pageType);
        const allowedInactivePageTypes = ['documents'];

        if (window.isAcceptingApps === false && !allowedInactivePageTypes.includes(pageType)) {
            step = AppSteps['notAccepting'];
        }

        return (
            <main className={mainClass}>
                <Head />
                <DialogContainer />
                <Header {...step} pageType={pageType} />
                <Steps />
                <ErrorBoundary>
                    {showStepLookup && <StepLookup step={step.comp} />}
                </ErrorBoundary>
                {overrideError ? null : this.renderError()}
                {loading && <Loader />}
                {!loading && !hideFooter && <CallMeFooter offerId={offerId} />}
                <Toast />
                <Normalize />
                {isCU && <MCCFooter />}
            </main>
        );
    }
}

const mapStateToProps = state => {
    const googleTagManagerId = get(state, 'creditor.googleTagManagerId');
    const {currentStep, previousY, isBack} = state.navigation;
    const {initialized, error, loading} = state.ui;
    const dialog = state.dialog.id;
    const dealType = get(state, 'creditor.type');

    return {
        currentStep,
        googleTagManagerId,
        initialized,
        error,
        loading,
        dialog,
        previousY,
        isBack,
        isCU: dealType === 'creditUnion',
        isDealer: dealType === 'dealer'
    };
};

export default connect(mapStateToProps, {fetchInfo})(
    injectSheet(styles)(App)
);
